import type React from "react";
import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";

import { useAuthStore } from "../state/authStore";
import "../styles/landing.css";

/* Self-serve signup (#253A/#254 + SimSense merge Phase 1). Dual-mode on ?path:
   - team (default): creates a team — no invite code, no iRacing customer ID.
   - solo: free individual account (backend adds a hidden comped personal team).
   The landing's two doors deep-link here; invite recipients are sent to the
   invite-gated /login flow. */
export default function Signup() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const solo = searchParams.get("path") === "solo";
  const { user, registerTeam, registerIndividual, loading, error } = useAuthStore();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [teamName, setTeamName] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [localError, setLocalError] = useState<string | null>(null);

  useEffect(() => {
    if (user) navigate("/teams", { replace: true });
  }, [user, navigate]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setLocalError(null);
    useAuthStore.setState({ error: null });
    if (!email.trim() || !email.includes("@")) return setLocalError("A valid email is required.");
    if (password.length < 6) return setLocalError("Password must be at least 6 characters.");
    if (!solo && teamName.trim().length < 2) return setLocalError("Give your team a name.");
    try {
      if (solo) {
        await registerIndividual(email.trim(), password, displayName.trim() || undefined);
      } else {
        await registerTeam(email.trim(), password, teamName.trim());
      }
      navigate("/teams", { replace: true });
    } catch {
      /* error surfaced via store */
    }
  };

  const displayError = localError || error;

  return (
    <div style={styles.page}>
      <div style={styles.backdropGlow} />
      <div style={styles.card}>
        <Link to="/" className="lp-wordmark" style={styles.wordmark} aria-label="StintSense home">
          StintSense<span className="lp-wordmark-led" aria-hidden="true" />
        </Link>

        <div className="eyebrow" style={{ marginBottom: "0.6rem" }}>{solo ? "One seat" : "Start your team"}</div>
        <h1 style={styles.title}>{solo ? "Create your free account" : "Set up your team"}</h1>
        <p style={styles.copy}>
          {solo
            ? "Free forever for one driver. No card."
            : "Create your team and invite your lineup. Free in early access. No card."}
        </p>

        {!solo && (
          <div style={styles.inviteNote}>
            Have an invite code? <Link to="/login" style={styles.inlineLink}>Join your team’s account →</Link>
          </div>
        )}

        <form onSubmit={handleSubmit} style={styles.form}>
          <label style={styles.field}>
            <span style={styles.label}>Email</span>
            <input
              className="input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              autoComplete="email"
              autoFocus
            />
          </label>
          <label style={styles.field}>
            <span style={styles.label}>Password</span>
            <input
              className="input"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="At least 6 characters"
              autoComplete="new-password"
            />
          </label>
          {solo ? (
            <label style={styles.field}>
              <span style={styles.label}>Driver name <span style={styles.optional}>(optional)</span></span>
              <input
                className="input"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder="How you show up on the sheet"
                autoComplete="name"
              />
            </label>
          ) : (
            <label style={styles.field}>
              <span style={styles.label}>Team name</span>
              <input
                className="input"
                value={teamName}
                onChange={(e) => setTeamName(e.target.value)}
                placeholder="e.g. Apex Racing"
                autoComplete="off"
              />
            </label>
          )}

          {displayError && <div style={styles.errorBanner} role="alert">{displayError}</div>}

          <button type="submit" className="btn btn-primary" disabled={loading} style={styles.submit}>
            {loading
              ? solo ? "Creating your account…" : "Creating your team…"
              : solo ? "Create free account" : "Set up your team"}
          </button>
        </form>

        <p style={styles.microline}>
          {solo ? "Free forever. No card. Windows + iRacing." : "Free in early access. No card. Windows + iRacing."}
        </p>
        <div style={styles.footerNote}>
          Already have an account? <Link to="/login" style={styles.inlineLink}>Sign in</Link>
        </div>
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  page: {
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "1.25rem",
    position: "relative",
    overflow: "hidden",
  },
  backdropGlow: {
    position: "absolute",
    inset: 0,
    background: "radial-gradient(circle at 50% 0%, var(--live-glow), transparent 38%)",
    pointerEvents: "none",
  },
  card: {
    position: "relative",
    zIndex: 1,
    width: "min(30rem, 100%)",
    background: "var(--ink-2)",
    border: "1px solid var(--rule)",
    borderRadius: "var(--r-lg)",
    padding: "2rem 1.75rem",
  },
  wordmark: { display: "inline-flex", fontSize: "1.1rem", marginBottom: "1.5rem", textDecoration: "none" },
  title: { margin: "0 0 0.45rem", fontSize: "1.5rem", fontWeight: 800, color: "var(--text-strong)" },
  copy: { margin: "0 0 1.4rem", color: "var(--text-secondary)", fontSize: "0.92rem", lineHeight: 1.5 },
  inviteNote: {
    marginBottom: "1.1rem",
    padding: "0.6rem 0.75rem",
    border: "1px solid var(--rule)",
    borderRadius: "var(--r-md)",
    fontSize: "0.85rem",
    color: "var(--text-secondary)",
  },
  form: { display: "grid", gap: "0.85rem" },
  field: { display: "grid", gap: "0.35rem" },
  label: { fontSize: "0.82rem", fontWeight: 600, color: "var(--text-secondary)" },
  optional: { fontWeight: 400, color: "var(--text-muted)" },
  errorBanner: {
    padding: "0.6rem 0.75rem",
    borderRadius: "var(--r-md)",
    border: "1px solid var(--flag-red, rgba(255,110,110,0.4))",
    background: "rgba(255, 80, 80, 0.08)",
    color: "var(--text-strong)",
    fontSize: "0.85rem",
  },
  submit: { width: "100%", marginTop: "0.3rem" },
  microline: { marginTop: "1rem", fontSize: "0.82rem", color: "var(--text-muted)", textAlign: "center" },
  footerNote: { marginTop: "0.85rem", fontSize: "0.88rem", color: "var(--text-secondary)", textAlign: "center" },
  inlineLink: { color: "var(--live-strong)", fontWeight: 600, textDecoration: "none" },
};
